import {
  AxesHelper,
  Clock,
  GridHelper,
  Group,
  MathUtils,
  Vector3,
  Vector4,
} from "three";
import { setQuery } from "./utils/query-params";
import renderer from "./renderer";
import { getRenderBufferSize, rendererSize, setRendererSize } from "./resize";
import settings from "./settings";
import { gui } from "./utils/gui";
import { rendererStats } from "./utils/stats";
import {
  createOrbitControls,
  resetCamera,
  updateCameras,
  devCamera,
  mainCamera,
} from "./cameras";
import scenes from "./scenes";
import Background from "./objects/background/background";
import Objkts from "./objects/objkts/objkts";
import curation from "./data/curation";
import content from "./data/content";
import { VECTOR_ZERO } from "./utils/math";
import cameraMovement, { CameraMovementConfig } from "./camera-movement";
import renderTargets from "./render-targets";
import detect from "@jam3/detect";

const contentData = content[curation.id];

export const OBJKTS_ENABLED = true;

export default class WebGLApp {
  constructor(parent) {
    parent.appendChild(renderer.domElement);

    this.clock = new Clock(true);
    this.rafId = 0;
    this.delta = 0;
    this.isRendering = false;

    const guiSettings = gui.addFolder("settings");
    guiSettings.open();

    guiSettings.add(settings, "devCamera").onChange((value) => {
      setQuery("devCamera", value);
    });

    const guiCamera = gui.addFolder("camera");
    guiCamera.open();

    guiCamera.add(mainCamera.position, "z", 0, 50);
    guiCamera.add(mainCamera, "fov", 1, 100).onChange(() => {
      mainCamera.updateProjectionMatrix();
    });

    resetCamera(devCamera, 5, new Vector3(0, 0.25, 1));

    mainCamera.position.fromArray(contentData.camera.position);
    mainCamera.lookAt(VECTOR_ZERO);

    this.controls = {
      dev: createOrbitControls(devCamera),
      main: createOrbitControls(mainCamera),
    };

    this.desktopControls = detect.device.isDesktop;

    this.controls.main.minDistance = mainCamera.position.z - 10;
    this.controls.main.maxDistance = mainCamera.position.z + 30;
    this.controls.main.minPolarAngle = MathUtils.degToRad(90 - 15);
    this.controls.main.maxPolarAngle = MathUtils.degToRad(90 + 15);
    this.controls.main.minAzimuthAngle = MathUtils.degToRad(-15);
    this.controls.main.maxAzimuthAngle = MathUtils.degToRad(15);
    this.controls.main.enableDamping = true;
    this.controls.main.enablePan = false;

    if (!this.desktopControls) {
      if (detect.device.isPhone || window.innerWidth < 1024) {
        mainCamera.position.z = this.controls.main.maxDistance;
      }
    } else {
      cameraMovement.setConfig(
        new CameraMovementConfig(10, 5, 0.115, mainCamera.position.z)
      );
      cameraMovement.setCamera(mainCamera);
      cameraMovement.setActive(true);
    }

    this.controls.main.enabled = !this.desktopControls;

    this.viewport = {
      debug: new Vector4(
        0,
        0,
        rendererSize.x * settings.viewportPreviewScale,
        rendererSize.y * settings.viewportPreviewScale
      ),
      main: new Vector4(0, 0, rendererSize.x, rendererSize.y),
    };

    this.helpers = new Group();
    this.helpers.add(new GridHelper(10, 10), new AxesHelper());
    this.helpers.visible = settings.helpers;
    scenes.main.add(this.helpers);

    guiSettings.add(settings, "helpers").onChange((value) => {
      this.helpers.visible = value;
      setQuery("helpers", value);
    });

    this.background = new Background(gui);
    scenes.main.add(this.background.mesh);
    scenes.background.add(this.background.mesh2);

    this.background.animate();

    if (OBJKTS_ENABLED) {
      this.objkts = new Objkts(gui);
      scenes.main.add(this.objkts.group);
      this.objkts.animate();
    }
  }

  resize = (width, height) => {
    setRendererSize(renderer, width, height);
    this.viewport.debug.set(
      0,
      0,
      rendererSize.x * settings.viewportPreviewScale,
      rendererSize.y * settings.viewportPreviewScale
    );

    this.viewport.main.set(0, 0, rendererSize.x, rendererSize.y);

    const { width: renderBufferWidth, height: renderBufferHeight } =
      getRenderBufferSize();

    renderTargets.background.setSize(renderBufferWidth, renderBufferHeight);

    if (OBJKTS_ENABLED) {
      this.objkts.resize(renderBufferWidth, renderBufferHeight);
    }

    updateCameras();
  };

  renderScene = (camera, viewport, delta) => {
    renderer.setViewport(viewport.x, viewport.y, viewport.z, viewport.w);
    renderer.setScissor(viewport.x, viewport.y, viewport.z, viewport.w);
    renderer.render(scenes.main, camera);
  };

  render = (render) => {
    if (this.isRendering === render) return;
    this.isRendering = render;
    if (render) {
      this.update();
    } else {
      cancelAnimationFrame(this.rafId);
    }
  };

  update = () => {
    this.rafId = requestAnimationFrame(this.update);
    this.delta = this.clock.getDelta();
    this.background.update(this.delta);

    if (OBJKTS_ENABLED) {
      this.objkts.update(this.delta);
    }

    if (this.desktopControls) {
      cameraMovement.update();
    } else {
      this.controls.main.update();
    }

    renderer.setRenderTarget(renderTargets.background);
    renderer.render(scenes.background, mainCamera);
    renderer.setRenderTarget(null);

    if (settings.devCamera) {
      this.renderScene(devCamera, this.viewport.main, this.delta);
      this.renderScene(mainCamera, this.viewport.debug, this.delta, true);
    } else {
      this.renderScene(mainCamera, this.viewport.main, this.delta);
    }

    if (settings.stats) {
      rendererStats.update(renderer);
    }
  };
}
